import axios from 'axios';
import { API_URL } from '../config';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Token ekleme
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - 401 durumunda logout
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (data: { email: string; password: string }) => api.post('/auth/login', data),
  register: (data: any) => api.post('/auth/register', data),
  getProfile: () => api.get('/auth/profile'),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/auth/change-password', data),
};

export const projectsAPI = {
  getCities: () => api.get('/projects/cities'),
  getDistricts: (provinceId: string) => api.get(`/projects/cities/${provinceId}/districts`),
  getAll: (params?: { cityId?: string; status?: string }) => api.get('/projects', { params }),
  getOne: (id: string) => api.get(`/projects/${id}`),
  getProjectPersonnel: (projectId: string) => api.get(`/projects/${projectId}/personnel`),
  // Proje Kullanıcı (Forman) Ataması
  getProjectUsers: (projectId: string) => api.get(`/projects/${projectId}/users`),
  assignUserToProject: (projectId: string, userId: string) =>
    api.post(`/projects/${projectId}/users/${userId}`),
  removeUserFromProject: (projectId: string, userId: string) =>
    api.delete(`/projects/${projectId}/users/${userId}`),
  create: (data: any) => api.post('/projects', data),
  update: (id: string, data: any) => api.patch(`/projects/${id}`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  getSites: (projectId?: string) => api.get('/projects/sites/all', { params: { projectId } }),
  getProjectSites: (projectId: string) => api.get('/projects/sites/all', { params: { projectId } }),
  getSite: (id: string) => api.get(`/projects/sites/${id}`),
  createSite: (data: any) => api.post('/projects/sites', data),
  updateSite: (id: string, data: any) => api.patch(`/projects/sites/${id}`, data),
  deleteSite: (id: string) => api.delete(`/projects/sites/${id}`),
  // Şantiye Personel
  getSitePersonnel: (siteId: string) => api.get(`/projects/sites/${siteId}/personnel`),
  getUnassignedPersonnel: (siteId: string) => api.get(`/projects/sites/${siteId}/personnel/unassigned`),
  assignPersonnelToSite: (siteId: string, personnelId: string) =>
    api.post(`/projects/sites/${siteId}/personnel/${personnelId}`),
  removePersonnelFromSite: (siteId: string, personnelId: string) =>
    api.delete(`/projects/sites/${siteId}/personnel/${personnelId}`),
  // Şantiye Kullanıcı (Forman) Ataması
  getSiteUsers: (siteId: string) => api.get(`/projects/sites/${siteId}/users`),
  assignUserToSite: (siteId: string, userId: string) =>
    api.post(`/projects/sites/${siteId}/users/${userId}`),
  removeUserFromSite: (siteId: string, userId: string) =>
    api.delete(`/projects/sites/${siteId}/users/${userId}`),
  getMySites: () => api.get('/projects/my-sites'),
  // Mahaller
  getSiteLocations: (siteId: string) => api.get(`/projects/sites/${siteId}/locations`),
  createLocation: (siteId: string, data: any) => api.post(`/projects/sites/${siteId}/locations`, data),
  updateLocation: (id: string, data: any) => api.patch(`/projects/locations/${id}`, data),
};

export const waybillsAPI = {
  getAll: (params?: {
    status?: string;
    siteId?: string;
    projectId?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }) => api.get('/waybills', { params }),
  getOne: (id: string) => api.get(`/waybills/${id}`),
  scanQR: (data: { waybillNumber: string; qrCode: string; siteId: string; notes?: string }) =>
    api.post('/waybills/scan', data),
  createManual: (data: any) => api.post('/waybills/manual', data),
  update: (id: string, data: any) => api.patch(`/waybills/${id}`, data),
  delete: (id: string) => api.delete(`/waybills/${id}`),
  getNotArrived: () => api.get('/waybills/not-arrived'),
  uploadPhotos: (id: string, formData: FormData) =>
    api.post(`/waybills/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Logo eşleştirme
  logoStatus: () => api.get('/waybills/logo/status'),
  logoMatchAll: () => api.post('/waybills/logo/match-all'),
  logoMatchOne: (waybillNumber: string) => api.post(`/waybills/logo/match/${waybillNumber}`),
  logoFind: (waybillNumber: string) => api.get(`/waybills/logo/find/${waybillNumber}`),
  logoList: (params?: { startDate?: string; endDate?: string; limit?: number; search?: string }) =>
    api.get('/waybills/logo/list', { params }),
  logoLines: (ficheno: string) => api.get(`/waybills/logo/lines/${ficheno}`),
};

export const dailyRecordsAPI = {
  getAll: (params?: {
    siteId?: string;
    startDate?: string;
    endDate?: string;
    isCompleted?: boolean;
  }) => api.get('/daily-records', { params }),
  getOne: (id: string) => api.get(`/daily-records/${id}`),
  create: (data: any) => api.post('/daily-records', data),
  update: (id: string, data: any) => api.patch(`/daily-records/${id}`, data),
  delete: (id: string) => api.delete(`/daily-records/${id}`),
  checkMissingDays: (siteId: string) => api.get(`/daily-records/missing-days/${siteId}`),
  checkRecordExists: (siteId: string, date: string) => api.get(`/daily-records/check/${siteId}/${date}`),
  uploadPhoto: (id: string, formData: FormData) =>
    api.post(`/daily-records/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  complete: (id: string) => api.patch(`/daily-records/${id}/complete`),
  toggleLock: (id: string) => api.patch(`/daily-records/${id}/toggle-lock`),
};

export const vehiclesAPI = {
  getAll: (params?: { type?: string; isActive?: boolean; isMachinery?: boolean }) => api.get('/vehicles', { params }),
  getOne: (id: string) => api.get(`/vehicles/${id}`),
  getBrandsAndModels: () => api.get('/vehicles/brands-models'),
  getUsageHistory: (id: string, limit?: number) => api.get(`/vehicles/${id}/usage-history`, { params: { limit } }),
  create: (data: any) => api.post('/vehicles', data),
  update: (id: string, data: any) => api.patch(`/vehicles/${id}`, data),
  delete: (id: string) => api.delete(`/vehicles/${id}`),
  // Proje ataması
  assignToProject: (id: string, projectId: string) => api.post(`/vehicles/${id}/assign/${projectId}`),
  removeFromProject: (id: string, projectId: string) => api.delete(`/vehicles/${id}/assign/${projectId}`),
  // Şantiye ataması
  getSiteVehicles: (siteId: string) => api.get(`/vehicles/site/${siteId}/list`),
  getUnassignedForSite: (siteId: string) => api.get(`/vehicles/site/${siteId}/unassigned`),
  assignToSite: (vehicleId: string, siteId: string) => api.post(`/vehicles/${vehicleId}/site/${siteId}`),
  removeFromSite: (vehicleId: string, siteId: string) => api.delete(`/vehicles/${vehicleId}/site/${siteId}`),
  // Evrak yönetimi
  getDocuments: (id: string) => api.get(`/vehicles/${id}/documents`),
  uploadDocument: (id: string, formData: FormData) => api.post(`/vehicles/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteDocument: (documentId: string) => api.delete(`/vehicles/documents/${documentId}`),
};

export const personnelAPI = {
  getAll: (params?: { projectId?: string; isActive?: boolean; title?: string; search?: string; status?: string }) =>
    api.get('/personnel', { params }),
  getOne: (id: string) => api.get(`/personnel/${id}`),
  getTitles: () => api.get('/personnel/titles'),
  create: (data: any) => api.post('/personnel', data),
  update: (id: string, data: any) => api.patch(`/personnel/${id}`, data),
  delete: (id: string) => api.delete(`/personnel/${id}`),
  getWageReport: (id: string, startDate: string, endDate: string) =>
    api.get(`/personnel/${id}/wages`, { params: { startDate, endDate } }),
  getWorkHistory: (id: string) => api.get(`/personnel/${id}/work-history`),
  getActivity: (id: string, limit?: number) => api.get(`/personnel/${id}/activity`, { params: { limit } }),
  // Proje ataması
  getProjectPersonnel: (projectId: string) => api.get(`/personnel/project/${projectId}/list`),
  getUnassignedForProject: (projectId: string) => api.get(`/personnel/project/${projectId}/unassigned`),
  assignToProject: (personnelId: string, projectId: string) => api.post(`/personnel/${personnelId}/assign/${projectId}`),
  removeFromProject: (personnelId: string, projectId: string) => api.delete(`/personnel/${personnelId}/assign/${projectId}`),
};

export const workItemsAPI = {
  getAll: (projectId?: string) => api.get('/work-items', { params: { projectId } }),
  getOne: (id: string) => api.get(`/work-items/${id}`),
  create: (data: any) => api.post('/work-items', data),
  update: (id: string, data: any) => api.patch(`/work-items/${id}`, data),
};

// Yeni İş Türleri API
export const workTypesAPI = {
  // İş Türleri
  getAll: (params?: { isActive?: boolean }) => api.get('/work-types', { params }),
  getUnits: () => api.get('/work-types/units'),
  getOne: (id: string) => api.get(`/work-types/${id}`),
  create: (data: { name: string; unit: string; description?: string }) =>
    api.post('/work-types', data),
  update: (id: string, data: { name?: string; unit?: string; description?: string; isActive?: boolean }) =>
    api.patch(`/work-types/${id}`, data),
  delete: (id: string) => api.delete(`/work-types/${id}`),

  // Proje İşleri
  getProjectWorks: (projectId: string) => api.get(`/work-types/project/${projectId}`),
  getUnassignedForProject: (projectId: string) => api.get(`/work-types/project/${projectId}/unassigned`),
  assignToProject: (projectId: string, data: { workTypeId: string; targetQuantity?: number; notes?: string }) =>
    api.post(`/work-types/project/${projectId}/assign`, data),
  updateProjectWork: (id: string, data: { targetQuantity?: number; notes?: string }) =>
    api.patch(`/work-types/project-work/${id}`, data),
  removeProjectWork: (id: string) => api.delete(`/work-types/project-work/${id}`),

  // Şantiye İşleri
  getSiteWorks: (siteId: string) => api.get(`/work-types/site/${siteId}`),
  getUnassignedForSite: (siteId: string) => api.get(`/work-types/site/${siteId}/unassigned`),
  getSiteWork: (id: string) => api.get(`/work-types/site-work/${id}`),
  assignToSite: (siteId: string, data: { workTypeId: string; targetQuantity?: number; notes?: string }) =>
    api.post(`/work-types/site/${siteId}/assign`, data),
  updateSiteWork: (id: string, data: { targetQuantity?: number; status?: string; notes?: string }) =>
    api.patch(`/work-types/site-work/${id}`, data),
  removeSiteWork: (id: string) => api.delete(`/work-types/site-work/${id}`),
  // Malzeme Eşleştirme
  searchLogoItems: (search: string) => api.get('/work-types/logo-items', { params: { search } }),
  getWorkTypeMaterials: (workTypeId: string) => api.get(`/work-types/${workTypeId}/materials`),
  addMaterial: (workTypeId: string, data: { logoItemCode: string; logoItemName: string }) =>
    api.post(`/work-types/${workTypeId}/materials`, data),
  removeMaterial: (materialId: string) => api.delete(`/work-types/materials/${materialId}`),
  getSiteWorkCost: (siteWorkId: string) => api.get(`/work-types/site-work/${siteWorkId}/cost`),
  // Proje Maliyet
  getProjectCost: (projectId: string) => api.get(`/work-types/project/${projectId}/cost`),
};

// Yakıt Kayıtları API
export const fuelAPI = {
  getAll: (params?: {
    vehicleId?: string;
    projectId?: string;
    siteId?: string;
    fuelType?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get('/fuel', { params }),
  getOne: (id: string) => api.get(`/fuel/${id}`),
  getStations: () => api.get('/fuel/stations'),
  getSummary: (params?: {
    vehicleId?: string;
    projectId?: string;
    siteId?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get('/fuel/summary', { params }),
  getVehicleFuelHistory: (vehicleId: string, limit?: number) =>
    api.get(`/fuel/vehicle/${vehicleId}`, { params: { limit } }),
  create: (data: {
    date: string;
    vehicleId: string;
    quantity: number;
    totalCost: number;
    unitPrice?: number;
    fuelType?: string;
    odometer?: number;
    station?: string;
    filledById?: string;
    invoiceNumber?: string;
    notes?: string;
    projectId?: string;
    siteId?: string;
  }) => api.post('/fuel', data),
  update: (id: string, data: Partial<{
    date: string;
    vehicleId: string;
    quantity: number;
    totalCost: number;
    unitPrice: number;
    fuelType: string;
    odometer: number;
    station: string;
    filledById: string;
    invoiceNumber: string;
    notes: string;
    projectId: string;
    siteId: string;
  }>) => api.patch(`/fuel/${id}`, data),
  delete: (id: string) => api.delete(`/fuel/${id}`),
};

export const personnelLeaveAPI = {
  getAll: (params?: {
    personnelId?: string;
    startDate?: string;
    endDate?: string;
    type?: string;
  }) => api.get('/personnel-leave', { params }),
  getOne: (id: string) => api.get(`/personnel-leave/${id}`),
  create: (data: { personnelId: string; date: string; type: string; notes?: string }) =>
    api.post('/personnel-leave', data),
  createBulk: (data: { personnelId: string; startDate: string; endDate: string; type: string; notes?: string }) =>
    api.post('/personnel-leave/bulk', data),
  update: (id: string, data: { type?: string; notes?: string }) =>
    api.patch(`/personnel-leave/${id}`, data),
  delete: (id: string) => api.delete(`/personnel-leave/${id}`),
};

export const reportsAPI = {
  getDashboardStats: (startDate: string, endDate: string) =>
    api.get('/reports/dashboard-stats', { params: { startDate, endDate } }),
  getDaily: (siteId: string, date: string) => api.get('/reports/daily', { params: { siteId, date } }),
  getDailySummary: (siteId: string, startDate: string, endDate: string) =>
    api.get('/reports/daily-summary', { params: { siteId, startDate, endDate } }),
  getMonthly: (siteId: string, year: number, month: number) =>
    api.get('/reports/monthly', { params: { siteId, year, month } }),
  getMonthlyWages: (siteId: string, month: number, year: number) =>
    api.get('/reports/monthly-wages', { params: { siteId, month, year } }),
  getPersonnelWages: (startDate: string, endDate: string, projectId?: string) =>
    api.get('/reports/personnel-wages', { params: { startDate, endDate, projectId } }),
  getVehicleUsage: (startDate: string, endDate: string) =>
    api.get('/reports/vehicle-usage', { params: { startDate, endDate } }),
  getWaybillMatch: (startDate: string, endDate: string, siteId?: string) =>
    api.get('/reports/waybill-match', { params: { startDate, endDate, siteId } }),
  getNotScanned: () => api.get('/reports/not-scanned'),
  getWorkProgress: () => api.get('/reports/work-progress'),
  getPersonnelHakedis: (params: {
    startDate: string;
    endDate: string;
    projectId?: string;
    siteId?: string;
  }) => api.get('/reports/personnel-hakedis', { params }),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getOne: (id: string) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.patch(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  getRoles: () => api.get('/users/roles'),
};

export const logsAPI = {
  getAll: (params?: { userId?: string; action?: string; entity?: string; limit?: number }) =>
    api.get('/logs', { params }),
};
